import { React, PropTypes, reduxConnect, css, styled } from '../lib/imports';
import { Spacing, SubmitButton } from '../components';
import { sendPlayerPicks, setIncompletePicksModalToClosed, clearPendingNavigation, handleModalLeave } from './actions';
import { stylingHelpers } from '../designTokens';
import { fireMessageEvent } from '../messenger/utils';
import IncompletePicksModal from './IncompletePicksModal';

const { disableVisually } = stylingHelpers;

const baseStyles = () => css`
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  width: 100%;
  text-align: right;
`;

const submittedStyles = ({ theme, isSubmitted }) =>
  isSubmitted &&
  css`
    margin-bottom: 0;
    padding: 0 ${theme.spacings.mega};
  `;

const Container = styled('div')(baseStyles, submittedStyles);

const confirmRowBaseStyles = ({ theme, adRendered, btnEnabled }) =>
  css`
    color: ${theme.colors.b900};
    margin-bottom: ${adRendered && !btnEnabled ? 0 : theme.spacings.mega};
    width: 100%;
  `;

const ConfirmRow = styled('div')(confirmRowBaseStyles);

const submitButtonStyles = ({ theme }) => css`
  font-size: 18px;
  &:disabled,
  &[disabled] {
    ${disableVisually()};
    background: ${theme.colors.n400};
    border-color: ${theme.colors.n400};
    color: ${theme.colors.b300};
  }
`;

const StyledSubmitButton = styled(SubmitButton)(submitButtonStyles);

const ConfirmPicks = ({
  buttonText,
  isSubmitted,
  btnEnabled,
  handlePickSubmission,
  adRendered,
  platform,
  picks,
  incompletePicksModalOpen,
  handleIncompletePicksModalClose,
  incompletePicksTitle,
  incompletePicksText,
  incompletePicksLeaveButtonText,
  incompletePicksCancelButtonText,
  incompletePicksLeaveAriaLabel,
  incompletePicksCancelAriaLabel,
  pendingNavigation,
  handleModalLeave,
  handleModalCancel,
}) => (
  <Container isSubmitted={isSubmitted}>
    <ConfirmRow adRendered={adRendered} btnEnabled={btnEnabled}>
      <Spacing sizeLeft='zetta' sizeRight='zetta'>
        <StyledSubmitButton
          variant='primary'
          text={buttonText}
          disabled={!btnEnabled}
          onSelect={() => {
            fireMessageEvent({
              type: 'LOG',
              platform,
              title: 'Submit Picks Button Clicked',
              data: {
                element: 'button',
                element_name: `Submit Picks`,
                picks,
              },
            });
            handlePickSubmission();
          }}
          stretch
        />
      </Spacing>
    </ConfirmRow>
    {incompletePicksModalOpen && (
      <IncompletePicksModal
        title={incompletePicksTitle}
        text={incompletePicksText}
        leaveButtonText={incompletePicksLeaveButtonText}
        cancelButtonText={incompletePicksCancelButtonText}
        onLeave={pendingNavigation ? handleModalLeave : handleIncompletePicksModalClose}
        onCancel={pendingNavigation ? handleModalCancel : handleIncompletePicksModalClose}
        leaveAriaLabel={incompletePicksLeaveAriaLabel}
        cancelAriaLabel={incompletePicksCancelAriaLabel}
      />
    )}
  </Container>
);

ConfirmPicks.propTypes = {
  buttonText: PropTypes.string,
  confirmText: PropTypes.string,
  btnEnabled: PropTypes.bool,
  isSubmitting: PropTypes.bool,
  isSubmitted: PropTypes.bool,
  isLarge: PropTypes.bool,
  isActive: PropTypes.bool,
  handlePickSubmission: PropTypes.func,
  adRendered: PropTypes.bool,
  platform: PropTypes.string,
  picks: PropTypes.object,
  incompletePicksModalOpen: PropTypes.bool,
  handleIncompletePicksModalClose: PropTypes.func,
  incompletePicksTitle: PropTypes.string,
  incompletePicksText: PropTypes.string,
  incompletePicksLeaveButtonText: PropTypes.string,
  incompletePicksCancelButtonText: PropTypes.string,
  incompletePicksLeaveAriaLabel: PropTypes.string,
  incompletePicksCancelAriaLabel: PropTypes.string,
  pendingNavigation: PropTypes.string,
  handleModalLeave: PropTypes.func,
  handleModalCancel: PropTypes.func,
};

ConfirmPicks.defaultProps = {
  btnEnabled: true,
  isSubmitted: false,
  isLarge: true,
  isActive: false,
  adRendered: false,
};

const mapStateToProps = (state) => {
  const { strings, picks, config } = state;
  const { platform } = config;

  const { picked, slotCount, incompletePicksModalOpen, pendingNavigation } = picks;

  const slotsPicked = Object.keys(picked)
    .map((id) => picked[id])
    .filter((id) => id);

  const allSlotsPicked = slotsPicked.length === slotCount;

  const allStrings = {
    buttonText: strings['button/confirmPlayers/text'],
    confirmText: strings['notifications/confirmPicks/text'],
    incompletePicksTitle: strings['notifications/incompletePicks/title'],
    incompletePicksText: strings['notifications/incompletePicks/text'],
    incompletePicksLeaveButtonText: strings['notifications/incompletePicks/leaveButton'],
    incompletePicksCancelButtonText: strings['notifications/incompletePicks/cancelButton'],
    incompletePicksLeaveAriaLabel: strings['roles/button/close/aria'],
    incompletePicksCancelAriaLabel: strings['roles/button/close/aria'],
  };

  return {
    ...allStrings,
    btnEnabled: allSlotsPicked,
    platform,
    picks,
    incompletePicksModalOpen,
    pendingNavigation,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    handlePickSubmission: (picks) => dispatch(sendPlayerPicks(picks)),
    handleIncompletePicksModalClose: () => dispatch(setIncompletePicksModalToClosed()),
    handleModalLeave: () => dispatch(handleModalLeave()),
    handleModalCancel: () => {
      dispatch(clearPendingNavigation());
      dispatch(setIncompletePicksModalToClosed());
    },
  };
};

export default reduxConnect(mapStateToProps, mapDispatchToProps)(ConfirmPicks);
